import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gapi } from "gapi-script";
import axios from "axios";
import "../welcome/welcome.scss";
import { setToken } from "../../services/userService";
import { isProfileComplete, cleanDisplayName } from "../../utils/profile";

function DeveloperWelcome() {
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const navigate = useNavigate();
  const API_BASE_URL = "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

  const persistAuthData = (authData) => {
    localStorage.setItem("token", authData.token);
    localStorage.setItem("immerselyEmail", authData.user.email);
    localStorage.setItem("userRole", authData.user.role || authData.user.userRole || "developer");
    setToken(authData.token);
    if (authData.user.name) {
      localStorage.setItem("name", cleanDisplayName(authData.user.name) || authData.user.name);
      localStorage.setItem("username", authData.user.username || "");
    }
  };

  const navigateByRole = (user) => {
    const role = (user.role || user.userRole || "developer").toLowerCase();
    const profileCompleted = isProfileComplete(user);

    if (role === "admin") {
      navigate("/admin");
      return;
    }

    if (role === "developer") {
      if (profileCompleted) {
        navigate("/developer");
      } else {
        navigate("/developer/welcome?profile_completed=false");
      }
      return;
    }

    if (profileCompleted) {
      navigate("/welcome?profile_completed=true");
    } else {
      navigate("/welcome?profile_completed=false");
    }
  };

  useEffect(() => {
    // Load the Google API client library
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.onload = () => {
      // Initialize the Google API client library
      gapi.load("auth2", () => {
        gapi.auth2.init({
          client_id:
            "1071233103855-orpereurl2c1fkikd5r9bclqecibdh9e.apps.googleusercontent.com",
          scope: "profile email",
          redirect_uri: "https://dashboard.immersely.ai/developer/welcome",
          cookiepolicy: "single_host_origin",
        });
      });
    };
    document.body.appendChild(script);
  }, []);

  const handleSignInClick = () => {
    setIsLoading(true);
    setAuthError("");
    gapi.auth2
      .getAuthInstance()
      .signIn()
      .then((data) => {
        axios
          .post(
            `${API_BASE_URL}/auth`,
            { 
              idToken: data.xc.id_token,
              role: "developer",
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              timeout: 60000,
            },
          )
          .then((data) => {
            persistAuthData(data.data);
            navigateByRole(data.data.user);
            setIsLoading(false);
          })
          .catch((error) => {
            setAuthError("Sign in failed. Please try again.");
            setIsLoading(false);
          });
      })
      .catch((error) => {
        setAuthError("Google sign in failed. Please try username and password.");
        setIsLoading(false);
      });
  };

  const handleLocalSignIn = async (e) => {
    e.preventDefault();
    setAuthError("");
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/local`,
        { username, password, role: "developer" },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          timeout: 60000,
        }
      );

      persistAuthData(response.data);
      navigateByRole(response.data.user);
    } catch (error) {
      setAuthError(error.response?.data?.message || "Username or password is incorrect.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modern-welcome-container">
      {/* Animated Background Elements */}
      <div className="background-elements">
        <div className="bg-element bg-element-1"></div>
        <div className="bg-element bg-element-2"></div>
        <div className="bg-element bg-element-3"></div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="content-wrapper">
          <div className="welcome-card">
            <div className="card-background"></div>
            
            <div className="card-header">
              <div className="header-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 12H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2"></path>
                  <path d="M18 12h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"></path>
                  <rect width="12" height="12" x="6" y="8" rx="2"></rect>
                </svg>
              </div>
              <h1 className="card-title">Welcome to Immersely Developer Portal</h1>
              <p className="card-subtitle">Sign in to access your developer dashboard</p>
            </div>

            <div className="card-content">
              <p className="welcome-description">
                Create and manage your games, track playtesting progress, and gather valuable feedback 
                from our community of playtesters. Sign in with Google or your local username and password.
              </p>

              {/* Google Sign-In Button */}
              <button
                onClick={handleSignInClick}
                disabled={isLoading}
                className="google-signin-btn"
              >
                <div className="btn-background"></div>
                <div className="btn-content">
                  <svg className="google-icon" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLoading ? "Signing in..." : "Sign in or create account with Google"}
                </div>
              </button>

              <div style={{ textAlign: "center", color: "#9ca3af", marginBottom: "1rem" }}>or</div>

              <form onSubmit={handleLocalSignIn} style={{ marginBottom: "1.5rem" }}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username or email"
                  disabled={isLoading}
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(255, 255, 255, 0.08)",
                    color: "white",
                    marginBottom: "0.75rem",
                  }}
                  required
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  disabled={isLoading}
                  style={{
                    width: "100%",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    background: "rgba(255, 255, 255, 0.08)",
                    color: "white",
                    marginBottom: "0.75rem",
                  }}
                  required
                />
                <button type="submit" disabled={isLoading} className="google-signin-btn" style={{ marginBottom: 0 }}>
                  <div className="btn-background"></div>
                  <div className="btn-content">{isLoading ? "Signing in..." : "Continue with Username & Password"}</div>
                </button>
                <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                  <Link to="/forgot-password?from=developer" style={{ color: "#e879f9", textDecoration: "none", fontSize: "0.875rem" }}>
                    Forgot password?
                  </Link>
                </div>
                <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                  <Link to="/create-account?from=developer" style={{ color: "#e879f9", textDecoration: "none", fontSize: "0.875rem" }}>
                    Create account with email
                  </Link>
                </div>
              </form>

              {authError && (
                <p style={{ color: "#fca5a5", fontSize: "0.875rem", marginBottom: "1rem", textAlign: "center" }}>
                  {authError}
                </p>
              )}

              {/* Link to Playtester Sign In */}
              <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Looking for the playtester portal?
                </p>
                <Link to="/" style={{ color: '#9333ea', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Sign in as Playtester →
                </Link>
              </div>

              {/* Security Notice */}
              <div className="security-notice">
                <div className="security-header">
                  <svg className="security-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4"/>
                    <path d="M21 12c-1.5-4.5-6-7.5-9-7.5s-7.5 3-9 7.5c1.5 4.5 6 7.5 9 7.5s7.5-3 9-7.5z"/>
                  </svg>
                  <span className="security-title">Secure Sign-In</span>
                </div>
                <p className="security-text">
                  Your data is protected with enterprise-grade security and encryption.
                </p>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="help-section">
            <p className="help-title">Need help?</p>
            <div className="help-links">
              <a href="mailto:support@immersely.ai" className="help-link">
                Contact Support
              </a>
              <span className="help-separator">•</span>
              <a href="/privacy" className="help-link">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeveloperWelcome;

